/**
 * EmailBlastPanel — real wired version of the email-blast composer.
 * Shows a dark-green admin banner under the "Clients can submit at
 * hepcure.ca/patient" strip. Tap it to compose or manage the recipient
 * list.
 *
 * All endpoints live under /api/email-blast/ and require the same master
 * PIN + password already used to unlock the /outreach app. Sends go
 * through the admin's own Resend account (configured once in Settings).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Mail, Send, Eye, ChevronDown, ChevronRight, Info, Lock, X, Search,
  Upload, Users, UserPlus, FileSpreadsheet, Check, Settings as SettingsIcon,
} from "lucide-react";
import { blastApi } from "@/lib/consult/emailBlastApi";

const DARK_GREEN = "#0F3B2E";
const LABEL = "#2D5A4C";
const MUTED = "#6B7A73";
const BORDER = "#E5E2DC";
const TEXT = "#1C2B26";
const TEAL = "#4F8274";
const RED = "#B4351B";

const monthLabel = (ym) => {
  try {
    const [y, m] = String(ym).split("-").map(Number);
    return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
  } catch { return ym; }
};

/**
 * Safely convert an axios/FastAPI error detail into a plain string for
 * toasts. FastAPI/Pydantic v2 returns validation errors as
 * `[{type, loc, msg, input, url}, …]` — passing that array to
 * `toast.error()` triggers React error #31 ("Objects are not valid as a
 * React child") which unmounts the whole app tree. Always coerce first.
 */
const errText = (detail, fallback = "Something went wrong") => {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    return detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ") || fallback;
  }
  if (typeof detail === "object") return detail.msg || JSON.stringify(detail);
  return String(detail);
};

export default function EmailBlastPanel() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("compose");  // compose | list | settings

  // Server state
  const [settings, setSettings] = useState(null);      // { configured, from_email, from_name }
  const [totals, setTotals] = useState({ all: 0, adherence: 0, imported: 0, opted_out: 0, active: 0 });
  const [recipients, setRecipients] = useState([]);
  const [months, setMonths] = useState([]);
  const [lastBroadcast, setLastBroadcast] = useState(null);
  const [lastBroadcastOpen, setLastBroadcastOpen] = useState(false);

  // Filters
  const [sourceFilter, setSourceFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Compose fields — persisted to localStorage so a half-drafted message
  // survives logout / refresh / a redeploy.
  const [subject, setSubject] = useState(() => {
    if (typeof window === "undefined") return "";
    try { return window.localStorage.getItem("hepcure.blast.subject") || ""; }
    catch { return ""; }
  });
  const [body, setBody] = useState(() => {
    if (typeof window === "undefined") return "";
    try { return window.localStorage.getItem("hepcure.blast.body") || ""; }
    catch { return ""; }
  });
  useEffect(() => {
    try { window.localStorage.setItem("hepcure.blast.subject", subject); } catch (_e) { /* quota — ignore */ }
  }, [subject]);
  useEffect(() => {
    try { window.localStorage.setItem("hepcure.blast.body", body); } catch (_e) { /* quota — ignore */ }
  }, [body]);

  // Test-send
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState(() => {
    if (typeof window === "undefined") return "";
    try { return window.localStorage.getItem("hepcure.blast.testEmail") || ""; }
    catch { return ""; }
  });
  const isValidTestEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((testEmail || "").trim());
  const rememberTestEmail = (v) => {
    setTestEmail(v);
    try { window.localStorage.setItem("hepcure.blast.testEmail", v); } catch (_e) { /* no-op */ }
  };

  // Import dialog
  const [importOpen, setImportOpen] = useState(false);
  const [importFile, setImportFile] = useState(null);   // { name, size }
  const [importPreview, setImportPreview] = useState(null); // { counts, rows, filename, size_bytes }
  const [importBusy, setImportBusy] = useState(false);
  const [importMode, setImportMode] = useState("file"); // "file" | "paste" — iOS-safe fallback
  const [importPaste, setImportPaste] = useState("");
  const fileInputRef = useRef(null);

  // Sending
  const [sending, setSending] = useState(false);

  // Resend rule — who gets skipped on this send: "days" (emailed in the
  // last N days, default 14), "all" (everyone ever emailed) or "none"
  // (send to the whole active list). Preview refetches on change.
  const [sendPreview, setSendPreview] = useState(null); // { cooldown_days, will_send_count, skipped_recent_count, skipped_recent: [...] }
  const [suppression, setSuppression] = useState("days");
  // Raw digits while typing (the field may be emptied mid-edit); clamped
  // to 1-365 via daysNum everywhere it's consumed.
  const [suppressionDays, setSuppressionDays] = useState("14");
  const daysNum = Math.max(1, Math.min(365, parseInt(suppressionDays, 10) || 14));
  const [showSkippedList, setShowSkippedList] = useState(false);

  // Settings form
  const [settingsForm, setSettingsForm] = useState({ resend_api_key: "", from_email: "", from_name: "HepCURE" });
  const [settingsBusy, setSettingsBusy] = useState(false);

  // ---------- data loaders ----------

  const reloadTotals = useCallback(async () => {
    try {
      const { data } = await blastApi.get("/recipients", {
        params: { source: "all", month: "all", search: "" },
      });
      setTotals(data.totals || {});
      setMonths(data.months || []);
    } catch (_e) { /* silent — banner still renders */ }
  }, []);

  const reloadRecipients = useCallback(async () => {
    try {
      // "opted-out" is a client-side lens on top of the full list — the
      // backend only knows about all/adherence/imported source buckets.
      const isOptedOutLens = sourceFilter === "opted-out";
      const backendSource = isOptedOutLens ? "all" : sourceFilter;
      const { data } = await blastApi.get("/recipients", {
        params: { source: backendSource, month: monthFilter, search },
      });
      const rows = data.recipients || [];
      setRecipients(isOptedOutLens ? rows.filter((r) => r.opted_out) : rows);
      setTotals(data.totals || {});
      setMonths(data.months || []);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Couldn't load recipients"));
    }
  }, [sourceFilter, monthFilter, search]);

  const reloadSettings = useCallback(async () => {
    try {
      const { data } = await blastApi.get("/settings");
      setSettings(data);
      setSettingsForm((f) => ({
        ...f,
        from_email: data.from_email || f.from_email,
        from_name: data.from_name || f.from_name,
      }));
    } catch (_e) { /* silent */ }
  }, []);

  const reloadLast = useCallback(async () => {
    try {
      const { data } = await blastApi.get("/last");
      setLastBroadcast(data);
    } catch (_e) { /* silent */ }
  }, []);

  const reloadSendPreview = useCallback(async () => {
    try {
      const { data } = await blastApi.get("/send-preview", {
        params: { suppression, days: daysNum },
      });
      setSendPreview(data);
    } catch (_e) { /* silent — banner just won't show */ }
  }, [suppression, suppressionDays]);

  // Initial + on-open load
  useEffect(() => { reloadTotals(); reloadSettings(); reloadLast(); }, [reloadTotals, reloadSettings, reloadLast]);
  useEffect(() => {
    if (open && view === "list") reloadRecipients();
    if (open && view === "compose") reloadSendPreview();
  }, [open, view, reloadRecipients, reloadSendPreview]);

  // ---------- actions ----------

  const activeCount = Math.max(0, (totals.active ?? 0));
  // What "Send" will actually deliver, given the resend rule. "Send to
  // everyone" skips nobody; otherwise the preview's skipped count applies.
  const cooldownSkip = suppression === "none" ? 0 : (sendPreview?.skipped_recent_count ?? 0);
  const effectiveSendCount = Math.max(0, activeCount - cooldownSkip);

  const openComposer = () => {
    if (!settings?.configured) {
      setView("settings");
    } else {
      setView("compose");
    }
    setOpen(true);
  };

  const saveSettings = async () => {
    setSettingsBusy(true);
    try {
      const payload = { ...settingsForm };
      if (!payload.resend_api_key) delete payload.resend_api_key;  // don't overwrite existing key with blank
      const { data } = await blastApi.post("/settings", payload);
      setSettings(data);
      setSettingsForm((f) => ({ ...f, resend_api_key: "" })); // never keep the key in state after save
      toast.success("Email settings saved");
      if (data.configured) setView("compose");
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Couldn't save settings"));
    } finally {
      setSettingsBusy(false);
    }
  };

  const toggleOptOut = async (email, next) => {
    // Optimistic update
    setRecipients((prev) => prev.map((r) => r.email === email ? { ...r, opted_out: next } : r));
    setTotals((t) => ({
      ...t,
      opted_out: (t.opted_out || 0) + (next ? 1 : -1),
      active:    (t.active    || 0) + (next ? -1 : 1),
    }));
    try {
      await blastApi.patch("/opt-out", { email, opted_out: next });
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Couldn't update opt-out"));
      reloadRecipients();
    }
  };

  const handleFileChosen = async (file) => {
    if (!file) return;
    // Client-side guardrails — reject huge files up front so the browser
    // can't hang before the backend has a chance to respond.
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File is too large (max 5 MB). Try splitting the list.");
      return;
    }

    // iOS Safari fix: materialize the file into memory BEFORE any state
    // updates or async operations. iOS revokes the file sandbox handle
    // when the tab is backgrounded (which happens when the native file
    // picker opens). Reading the file into an ArrayBuffer copies the
    // bytes into the JS heap so it's immune to iOS handle revocation.
    let safeFile;
    try {
      const buffer = await file.arrayBuffer();
      safeFile = new File([buffer], file.name, {
        type: file.type,
        lastModified: file.lastModified,
      });
    } catch (err) {
      toast.error("Couldn't read the file. Please try again.");
      console.error("[EmailBlast] File materialization failed:", err);
      return;
    }

    setImportFile({ name: safeFile.name, size: safeFile.size });
    setImportBusy(true);
    setImportPreview(null);
    try {
      const form = new FormData();
      form.append("file", safeFile);
      // Do NOT set Content-Type manually — axios/browser must set it
      // automatically so the correct `boundary=…` parameter is included.
      // A bare "multipart/form-data" header (no boundary) makes iOS Safari
      // send an unparseable body and the server returns 422.
      const { data } = await blastApi.post("/import/preview", form, {
        timeout: 45000,  // 45s ceiling; Cloudflare's edge timeout is 100s
      });
      // Defensive shape check — if a proxy returned HTML instead of JSON,
      // data will be a string and rendering it would crash the panel.
      if (!data || typeof data !== "object" || !data.counts || !Array.isArray(data.rows)) {
        toast.error("Import server returned an unexpected response. Try again.");
        setImportFile(null);
        return;
      }
      setImportPreview(data);
    } catch (e) {
      const msg =
        e?.code === "ECONNABORTED"
          ? "Upload timed out. Try a smaller list or check your connection."
          : errText(e?.response?.data?.detail, e?.message || "Couldn't parse file");
      toast.error(msg);
      setImportFile(null);
    } finally {
      setImportBusy(false);
    }
  };

  /**
   * Paste-mode ingest — for browsers (notably iOS Safari) that go blank on
   * file-input handling. Turns pasted text into a synthetic CSV file and
   * pushes it through the same server endpoint so classification logic is
   * identical to the file path.
   */
  const handlePasteImport = async () => {
    const raw = (importPaste || "").trim();
    if (!raw) {
      toast.error("Paste at least one email");
      return;
    }
    // Build a CSV blob — one row per line, first column = email.
    const csv = "email\n" + raw
      .split(/[\r\n,;]+/)
      .map((s) => s.trim())
      .filter(Boolean)
      .join("\n");
    if (csv.length > 5 * 1024 * 1024) {
      toast.error("Pasted text is too large (max 5 MB).");
      return;
    }
    const blob = new Blob([csv], { type: "text/csv" });
    // Materialize into a File so paste-mode goes through the exact same
    // code path as file-mode (helps in iOS WebViews too).
    const buffer = await blob.arrayBuffer();
    const safeFile = new File([buffer], "pasted-list.csv", { type: "text/csv" });
    setImportFile({ name: safeFile.name, size: safeFile.size });
    setImportBusy(true);
    setImportPreview(null);
    try {
      const form = new FormData();
      form.append("file", safeFile, "pasted-list.csv");
      const { data } = await blastApi.post("/import/preview", form, {
        timeout: 45000,
      });
      if (!data || typeof data !== "object" || !data.counts || !Array.isArray(data.rows)) {
        toast.error("Import server returned an unexpected response. Try again.");
        setImportFile(null);
        return;
      }
      setImportPreview(data);
    } catch (e) {
      const msg = e?.code === "ECONNABORTED"
        ? "Upload timed out. Try a smaller list."
        : errText(e?.response?.data?.detail, e?.message || "Couldn't parse pasted list");
      toast.error(msg);
      setImportFile(null);
    } finally {
      setImportBusy(false);
    }
  };

  const confirmImport = async () => {
    if (!importPreview?.rows?.length) return;
    setImportBusy(true);
    try {
      // Only send NEW rows to the server — server also filters, but this keeps the wire payload small.
      const rows = importPreview.rows.filter((r) => r.status === "new");
      const { data } = await blastApi.post("/import/confirm", { rows });
      toast.success(`Added ${data.added} recipients`);
      setImportOpen(false);
      setImportFile(null);
      setImportPreview(null);
      await reloadRecipients();
      await reloadTotals();
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Couldn't import list"));
    } finally {
      setImportBusy(false);
    }
  };

  const sendTest = async () => {
    if (!isValidTestEmail || !subject.trim() || !body.trim()) return;
    try {
      await blastApi.post("/send", {
        subject: subject.trim(),
        body: body.trim(),
        exclude_emails: [],
        test_email: testEmail.trim(),
      });
      toast.success(`Test sent to ${testEmail.trim()}`);
      setTestOpen(false);
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Test send failed"));
    }
  };

  const sendBroadcast = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Subject and message are required");
      return;
    }
    // Effective count = active minus resend-rule skips.
    const cooldownSkipped = suppression === "none" ? 0 : (sendPreview?.skipped_recent_count ?? 0);
    const effectiveCount = Math.max(0, activeCount - cooldownSkipped);
    if (effectiveCount === 0) {
      toast.error("No active recipients");
      return;
    }
    // Simple confirm — a fancy modal here would be overkill for this use.
    const skipNote = cooldownSkipped > 0
      ? `\n(${cooldownSkipped} skipped — ${suppression === "all" ? "already emailed" : `emailed in the last ${daysNum} days`})`
      : "";
    const ok = typeof window !== "undefined"
      ? window.confirm(`Send this message to ${effectiveCount} recipients?${skipNote}`)
      : true;
    if (!ok) return;
    setSending(true);
    try {
      const { data } = await blastApi.post("/send", {
        subject: subject.trim(),
        body: body.trim(),
        exclude_emails: [],
        include_recent: suppression === "none",
        suppression,
        suppression_days: daysNum,
      });
      const skipStr = data.skipped_recent ? ` · ${data.skipped_recent} skipped` : "";
      toast.success(`Sent to ${data.sent} recipients${data.failed ? ` · ${data.failed} failed` : ""}${skipStr}`);
      setOpen(false);
      // Keep the template for next time (persisted) — user asked drafts stay.
      // Reset the resend rule to the safe default after send.
      setSuppression("days");
      setSuppressionDays("14");
      await reloadLast();
      await reloadSendPreview();
    } catch (e) {
      toast.error(errText(e?.response?.data?.detail, "Send failed"));
    } finally {
      setSending(false);
    }
  };

  // ---------- rendering ----------

  return (
    <>
      {/* Admin CTA banner */}
      <button
        type="button"
        onClick={openComposer}
        data-testid="email-blast-open"
        style={{
          width: "100%", textAlign: "left",
          display: "flex", alignItems: "center", gap: 10,
          background: DARK_GREEN, border: "none",
          borderRadius: 10, padding: "10px 12px", marginBottom: 14,
          color: "white", cursor: "pointer",
          fontFamily: "inherit", fontSize: 13,
          transition: "opacity 0.15s ease",
          boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
        }}
      >
        <div style={{
          width: 30, height: 30, borderRadius: 8,
          background: "rgba(255,255,255,0.15)",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "white", flexShrink: 0,
        }}>
          <Mail style={{ width: 15, height: 15 }} strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 14, fontWeight: 700, color: "white", lineHeight: 1.25,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            Email the recipient list
          </div>
          <div style={{
            fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            <Lock style={{ width: 9, height: 9, display: "inline", marginRight: 3, verticalAlign: "-1px" }} strokeWidth={2.6} />
            Admin only · {(totals.active ?? 0).toLocaleString()} active recipient{(totals.active ?? 0) === 1 ? "" : "s"}
            {!settings?.configured && " · setup needed"}
          </div>
        </div>
        <ChevronRight style={{ width: 16, height: 16, color: "rgba(255,255,255,0.75)", flexShrink: 0 }} strokeWidth={2.2} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="email-blast-dialog"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
          style={{
            position: "fixed", inset: 0, zIndex: 90,
            background: "rgba(15,20,17,0.55)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 12,
          }}
        >
          <div style={{
            background: "white", borderRadius: 16,
            width: "min(640px, 100%)", maxHeight: "92vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              padding: "16px 20px", borderBottom: `1px solid ${BORDER}`,
              display: "flex", alignItems: "flex-start", gap: 12,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: DARK_GREEN, color: "white",
                display: "flex", alignItems: "center", justifyContent: "center",
                flexShrink: 0,
              }}>
                <Mail style={{ width: 20, height: 20 }} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, lineHeight: 1.2 }}>
                  {view === "settings" ? "Email settings" : "Broadcast recipient list"}
                </div>
                {view === "settings" ? (
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>
                    Configure your Resend account and sender.
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: MUTED, marginTop: 3, lineHeight: 1.4 }}>
                    <div>From <b style={{ color: TEXT }}>{settings?.from_email || "(not configured)"}</b></div>
                    <div>Send to <b style={{ color: TEXT }}>{activeCount}</b> active client{activeCount === 1 ? "" : "s"}</div>
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => setView("settings")}
                title="Settings"
                aria-label="Settings"
                style={{
                  background: view === "settings" ? "#EEF2EF" : "none",
                  border: `1px solid ${view === "settings" ? DARK_GREEN : "transparent"}`,
                  padding: 6, color: view === "settings" ? DARK_GREEN : MUTED,
                  cursor: "pointer", borderRadius: 6,
                }}
              >
                <SettingsIcon style={{ width: 16, height: 16 }} strokeWidth={2.2} />
              </button>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 6 }}>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  style={{
                    background: "none", border: "none", padding: 6,
                    color: MUTED, cursor: "pointer", borderRadius: 6,
                  }}
                >
                  <X style={{ width: 16, height: 16 }} strokeWidth={2.2} />
                </button>
                {view === "compose" && (
                  <button
                    type="button"
                    onClick={() => setView("list")}
                    data-testid="email-blast-toggle-list"
                    style={{
                      background: "none", border: `1px dashed ${BORDER}`,
                      padding: "3px 9px", borderRadius: 999,
                      fontSize: 10.5, fontWeight: 700, color: LABEL, cursor: "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    View list →
                  </button>
                )}
              </div>
            </div>

            {/* Recipient summary strip removed — "Back to compose" now lives
                below the X close button; the recipient counts are already
                visible on each source chip and in the header block. */}

            {/* ================= COMPOSE VIEW ================= */}
            {view === "compose" && (
              <div style={{
                padding: 20, flex: 1, minHeight: 0, overflowY: "auto",
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                display: "grid", gap: 14, alignContent: "start",
              }}>
                <div>
                  <label style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em",
                    textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4,
                  }}>From</label>
                  <div style={{
                    height: 38, display: "flex", alignItems: "center",
                    padding: "0 12px", borderRadius: 8,
                    background: "#F7F6F2", border: `1px solid ${BORDER}`,
                    color: MUTED, fontSize: 13,
                  }}>
                    <Lock style={{ width: 12, height: 12, marginRight: 6 }} strokeWidth={2.2} />
                    {settings?.from_email || "(not configured)"}
                  </div>
                </div>

                <div>
                  <label style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em",
                    textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4,
                  }}>Subject</label>
                  <input
                    type="text"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    placeholder="e.g. This month's reward — book your bloodwork"
                    data-testid="email-blast-subject"
                    style={{
                      width: "100%", height: 38, boxSizing: "border-box",
                      padding: "0 12px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: "white",
                      color: TEXT, fontSize: 14, fontFamily: "inherit",
                    }}
                  />
                </div>

                <div>
                  <label style={{
                    fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em",
                    textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4,
                  }}>Message</label>
                  <div style={{
                    border: `1px solid ${BORDER}`, borderRadius: 8,
                    background: "white", overflow: "hidden",
                  }}>
                    <div style={{ padding: "10px 12px 6px", color: TEXT, fontSize: 14, lineHeight: 1.4, display: "flex", alignItems: "center", gap: 8 }}>
                      <span>Hi <b>[first name]</b>,</span>
                      <span style={{
                        fontSize: 10, fontWeight: 800, letterSpacing: "0.14em",
                        textTransform: "uppercase", color: MUTED,
                        background: "#F1EFE8", padding: "2px 6px", borderRadius: 999,
                        display: "inline-flex", alignItems: "center", gap: 3,
                      }}>
                        <Lock style={{ width: 9, height: 9 }} strokeWidth={2.4} /> Auto
                      </span>
                    </div>
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder={"This is a friendly reminder that we now pay $40 for a completed bloodwork visit. Just call 1-844-925-CURE or submit your request at hepcure.ca/patient."}
                      rows={7}
                      data-testid="email-blast-body"
                      style={{
                        width: "100%", boxSizing: "border-box",
                        padding: "4px 12px 8px", margin: 0,
                        border: "none", outline: "none", background: "transparent",
                        color: TEXT, fontSize: 14, fontFamily: "inherit",
                        lineHeight: 1.4, resize: "vertical", minHeight: 140,
                      }}
                    />
                    <div style={{ padding: "4px 12px 12px", color: TEXT, fontSize: 14, lineHeight: 1.4, display: "flex", flexDirection: "column", gap: 8 }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        <span>— The HepCURE team</span>
                        <span style={{
                          fontSize: 10, fontWeight: 800, letterSpacing: "0.14em",
                          textTransform: "uppercase", color: MUTED,
                          background: "#F1EFE8", padding: "2px 6px", borderRadius: 999,
                          display: "inline-flex", alignItems: "center", gap: 3,
                        }}>
                          <Lock style={{ width: 9, height: 9 }} strokeWidth={2.4} /> Auto
                        </span>
                      </div>
                      <div style={{ fontSize: 13, color: MUTED, lineHeight: 1.5 }}>
                        If you know someone who needs treatment, please reply to this email with their contact information or call us at{" "}
                        <b style={{ color: TEXT }}>1-844-925-2873</b>.
                      </div>
                      <div style={{ fontSize: 12, color: MUTED }}>
                        <span style={{ textDecoration: "underline" }}>unsubscribe</span> link + mailing address appended for CASL.
                      </div>
                    </div>
                  </div>
                  <div style={{ marginTop: 6, fontSize: 11, color: MUTED, textAlign: "right" }}>
                    {body.length} chars
                  </div>
                </div>

                {/* Resend rule — pick who this send skips */}
                <div
                  data-testid="email-blast-suppression"
                  style={{
                    border: `1px solid ${BORDER}`, borderRadius: 10, padding: "10px 12px",
                    display: "grid", gap: 8, background: "white",
                  }}
                >
                  <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, color: MUTED, textTransform: "uppercase" }}>
                    Resend rule
                  </div>
                  <div style={{ display: "grid", gap: 6 }}>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      <button
                        type="button"
                        onClick={() => setSuppression("days")}
                        data-testid="email-blast-suppression-days-pill"
                        style={{
                          padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                          border: `1.5px solid ${suppression === "days" ? DARK_GREEN : BORDER}`,
                          background: suppression === "days" ? DARK_GREEN : "white",
                          color: suppression === "days" ? "white" : TEXT,
                          cursor: "pointer", fontFamily: "inherit",
                        }}
                      >
                        Skip recently sent
                      </button>
                      {suppression === "days" && (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, color: MUTED }}>
                          last
                          <input
                            type="text" inputMode="numeric" pattern="[0-9]*" value={suppressionDays}
                            onChange={(e) => setSuppressionDays(e.target.value.replace(/\D/g, "").slice(0, 3))}
                            onBlur={() => setSuppressionDays(String(daysNum))}
                            data-testid="email-blast-suppression-days"
                            style={{
                              width: 56, padding: "4px 6px", borderRadius: 7,
                              border: `1px solid ${BORDER}`, fontSize: 12.5, fontFamily: "inherit",
                            }}
                          />
                          days
                        </span>
                      )}
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                      {[
                        { v: "all", label: "Skip everyone already sent" },
                        { v: "none", label: "Send to everyone" },
                      ].map((o) => (
                        <button
                          key={o.v}
                          type="button"
                          onClick={() => setSuppression(o.v)}
                          data-testid={`email-blast-suppression-${o.v}`}
                          style={{
                            padding: "6px 12px", borderRadius: 999, fontSize: 12, fontWeight: 700,
                            border: `1.5px solid ${suppression === o.v ? DARK_GREEN : BORDER}`,
                            background: suppression === o.v ? DARK_GREEN : "white",
                            color: suppression === o.v ? "white" : TEXT,
                            cursor: "pointer", fontFamily: "inherit",
                          }}
                        >
                          {o.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Skipped-recipients banner — shown when the resend rule
                    actually excludes people on this send. */}
                {sendPreview && suppression !== "none" && sendPreview.skipped_recent_count > 0 && (
                  <div
                    data-testid="email-blast-cooldown-banner"
                    style={{
                      background: "#F5F1E5", border: "1px solid #D9C99A",
                      borderRadius: 10, padding: "12px 14px",
                      display: "grid", gap: 8, fontSize: 13, color: "#5A4300",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                      <Info style={{ width: 14, height: 14, flexShrink: 0, marginTop: 2 }} strokeWidth={2.2} />
                      <div style={{ lineHeight: 1.45 }}>
                        <b>{sendPreview.skipped_recent_count} recipient{sendPreview.skipped_recent_count === 1 ? "" : "s"}</b>{" "}
                        {suppression === "all"
                          ? "have already received a broadcast"
                          : <>already received a broadcast in the last <b>{sendPreview.cooldown_days} days</b></>}
                        . They&rsquo;ll be skipped —{" "}
                        <b style={{ color: DARK_GREEN }}>{sendPreview.will_send_count}</b>{" "}
                        will actually receive this send.
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => setShowSkippedList((v) => !v)}
                        data-testid="email-blast-view-skipped"
                        style={{
                          background: "white", border: `1px solid ${BORDER}`,
                          padding: "5px 10px", borderRadius: 8,
                          fontSize: 12, fontWeight: 700, color: TEXT, cursor: "pointer",
                          display: "inline-flex", alignItems: "center", gap: 5,
                        }}
                      >
                        {showSkippedList ? "Hide skipped list" : `View skipped list (${sendPreview.skipped_recent_count})`}
                      </button>
                    </div>
                    {showSkippedList && (
                      <div
                        data-testid="email-blast-skipped-list"
                        style={{
                          background: "white", border: `1px solid ${BORDER}`,
                          borderRadius: 8, overflow: "hidden",
                          maxHeight: 240, overflowY: "auto",
                        }}
                      >
                        {sendPreview.skipped_recent.map((row, i) => (
                          <div
                            key={row.email + i}
                            style={{
                              display: "flex", alignItems: "center", justifyContent: "space-between",
                              padding: "6px 10px", borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                              fontSize: 12, color: TEXT,
                            }}
                          >
                            <span style={{
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>{row.email}</span>
                            <span style={{ fontSize: 11, color: MUTED, marginLeft: 8, flexShrink: 0 }}>
                              {(row.last_sent_at || "").slice(0, 10)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div style={{
                  background: "#FFF8E9", border: "1px solid #E9D9A8",
                  borderRadius: 8, padding: "10px 12px",
                  display: "flex", alignItems: "flex-start", gap: 8,
                  fontSize: 12, color: "#5A4300", lineHeight: 1.4,
                }}>
                  <Info style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} strokeWidth={2.2} />
                  <div>
                    An <b>unsubscribe link</b> and our mailing address are appended
                    automatically to every message (CASL compliance).
                  </div>
                </div>

                {lastBroadcast && (
                  <div
                    data-testid="email-blast-last-card"
                    style={{
                      border: `1px solid ${BORDER}`, borderRadius: 8,
                      fontSize: 12, color: MUTED, overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      onClick={() => setLastBroadcastOpen((v) => !v)}
                      data-testid="email-blast-last-toggle"
                      aria-expanded={lastBroadcastOpen}
                      style={{
                        width: "100%", background: "transparent", border: "none",
                        padding: 12, textAlign: "left", cursor: "pointer",
                        display: "flex", alignItems: "center", gap: 10,
                        fontFamily: "inherit", color: MUTED,
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontWeight: 700, color: LABEL, marginBottom: 3,
                          letterSpacing: "0.14em", textTransform: "uppercase", fontSize: 10.5,
                        }}>
                          Last broadcast
                        </div>
                        <div style={{
                          color: TEXT, fontWeight: 600, overflow: "hidden",
                          textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>{lastBroadcast.subject}</div>
                        <div>
                          {(lastBroadcast.sent_at || "").slice(0, 10)} · {lastBroadcast.sent_count} recipients
                          {lastBroadcast.failed_count ? ` · ${lastBroadcast.failed_count} failed` : ""}
                        </div>
                      </div>
                      <ChevronDown
                        style={{
                          width: 16, height: 16, flexShrink: 0, color: MUTED,
                          transform: lastBroadcastOpen ? "rotate(180deg)" : "none",
                          transition: "transform 160ms ease",
                        }}
                        strokeWidth={2.2}
                      />
                    </button>
                    {lastBroadcastOpen && (
                      <div
                        data-testid="email-blast-last-body"
                        style={{
                          borderTop: `1px solid ${BORDER}`, background: "#FAFAF7",
                          padding: 12, color: TEXT, lineHeight: 1.5, fontSize: 13,
                          whiteSpace: "pre-wrap", wordBreak: "break-word",
                          maxHeight: 260, overflowY: "auto",
                          overscrollBehavior: "contain",
                        }}
                      >
                        {lastBroadcast.body || <span style={{ color: MUTED, fontStyle: "italic" }}>(no body captured)</span>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ================= LIST VIEW ================= */}
            {view === "list" && (
              <div style={{
                padding: 16, flex: 1, minHeight: 0, overflowY: "auto",
                // Contain scroll to this pane on iOS Safari — without this,
                // reaching the top or bottom of the list bounces the whole
                // web page in the background.
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                display: "flex", flexDirection: "column", gap: 10,
              }}>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <div role="tablist" style={{
                    display: "inline-flex", background: "#F1EFE8",
                    borderRadius: 999, padding: 3, gap: 2,
                  }}>
                    {[
                      { key: "all",       label: `All · ${totals.all ?? 0}`,        icon: null },
                      { key: "adherence", label: `Adherence · ${totals.adherence ?? 0}`, icon: Users },
                      { key: "opted-out", label: `Opted out · ${totals.opted_out ?? 0}`, icon: Lock },
                    ].map((tt) => {
                      const active = sourceFilter === tt.key;
                      const IconEl = tt.icon;
                      return (
                        <button
                          key={tt.key}
                          type="button"
                          onClick={() => setSourceFilter(tt.key)}
                          data-testid={`email-blast-source-${tt.key}`}
                          style={{
                            display: "inline-flex", alignItems: "center", gap: 4,
                            padding: "4px 10px", borderRadius: 999,
                            border: "none", cursor: "pointer",
                            background: active ? "white" : "transparent",
                            color: active ? DARK_GREEN : MUTED,
                            fontSize: 11.5, fontWeight: 700, fontFamily: "inherit",
                            boxShadow: active ? "0 1px 2px rgba(15,59,46,0.12)" : "none",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {IconEl && <IconEl style={{ width: 11, height: 11 }} strokeWidth={2.4} />}
                          {tt.label}
                        </button>
                      );
                    })}
                  </div>
                  <div style={{ flex: 1 }} />
                  <button
                    type="button"
                    onClick={() => { setImportOpen(true); setImportFile(null); setImportPreview(null); }}
                    data-testid="email-blast-import-open"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      background: "white", border: `1px solid ${DARK_GREEN}`,
                      color: DARK_GREEN, fontSize: 12, fontWeight: 700,
                      height: 30, padding: "0 12px", borderRadius: 999,
                      cursor: "pointer", whiteSpace: "nowrap",
                    }}
                  >
                    <UserPlus style={{ width: 13, height: 13 }} strokeWidth={2.2} />
                    Import list
                  </button>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <select
                    value={monthFilter}
                    onChange={(e) => setMonthFilter(e.target.value)}
                    style={{
                      height: 34, padding: "0 10px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: "white",
                      color: TEXT, fontSize: 13, fontFamily: "inherit", minWidth: 140,
                    }}
                  >
                    <option value="all">All months</option>
                    {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
                  </select>
                  <div style={{ flex: 1, minWidth: 140, position: "relative", display: "flex", alignItems: "center" }}>
                    <Search style={{ position: "absolute", left: 10, width: 13, height: 13, color: MUTED }} strokeWidth={2.2} />
                    <input
                      type="text"
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search name or email…"
                      style={{
                        width: "100%", height: 34, boxSizing: "border-box",
                        padding: "0 10px 0 28px", borderRadius: 8,
                        border: `1px solid ${BORDER}`, background: "white",
                        color: TEXT, fontSize: 13, fontFamily: "inherit",
                      }}
                    />
                  </div>
                </div>

                <div style={{ fontSize: 11, color: MUTED, marginTop: -2 }}>
                  Showing <b style={{ color: TEXT }}>{recipients.length}</b> of{" "}
                  {sourceFilter === "opted-out"
                    ? (totals.opted_out ?? 0)
                    : (totals[sourceFilter === "all" ? "all" : sourceFilter] ?? 0)}
                </div>

                <div style={{ border: `1px solid ${BORDER}`, borderRadius: 10, overflow: "hidden", background: "white" }}>
                  {recipients.length === 0 && (
                    <div style={{ padding: 20, textAlign: "center", color: MUTED, fontSize: 12 }}>
                      No recipients match your filters.
                    </div>
                  )}
                  {recipients.map((r) => (
                    <div
                      key={r.email}
                      style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 12px", borderTop: `1px solid ${BORDER}`,
                        opacity: r.opted_out ? 0.5 : 1, flexWrap: "nowrap",
                      }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{
                          fontSize: 13, color: TEXT, fontWeight: 600,
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          textDecoration: r.opted_out ? "line-through" : "none",
                          display: "flex", alignItems: "center", gap: 6,
                        }}>
                          {r.name ? r.name : (
                            <span style={{ color: MUTED, fontWeight: 500, fontStyle: "italic" }}>{r.email}</span>
                          )}
                          {r.source === "imported" && (
                            <span style={{
                              fontSize: 9.5, fontWeight: 800, letterSpacing: "0.12em",
                              textTransform: "uppercase", color: "#3E5B96",
                              background: "rgba(62,91,150,0.10)",
                              padding: "1px 6px", borderRadius: 999, flexShrink: 0,
                            }}>Imported</span>
                          )}
                        </div>
                        {r.name && (
                          <div style={{
                            fontSize: 11.5, color: MUTED, marginTop: 1,
                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                          }}>
                            {r.email}
                          </div>
                        )}
                      </div>
                      {r.opted_out ? (
                        <button
                          type="button"
                          onClick={() => toggleOptOut(r.email, false)}
                          aria-label="Re-add"
                          style={{
                            flexShrink: 0,
                            background: "white", border: `1px solid ${BORDER}`,
                            color: DARK_GREEN, fontSize: 11, fontWeight: 700,
                            height: 28, padding: "0 10px", borderRadius: 999, cursor: "pointer", whiteSpace: "nowrap",
                          }}
                        >
                          + Re-add
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => toggleOptOut(r.email, true)}
                          aria-label="Exclude"
                          style={{
                            flexShrink: 0,
                            background: "white", border: `1px solid ${BORDER}`,
                            color: RED, fontSize: 11, fontWeight: 700,
                            height: 28, width: 28, padding: 0, borderRadius: 999, cursor: "pointer",
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                          }}
                        >
                          <X style={{ width: 13, height: 13 }} strokeWidth={2.6} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ================= SETTINGS VIEW ================= */}
            {view === "settings" && (
              <div style={{ padding: 20, flex: 1, minHeight: 0, overflowY: "auto", display: "grid", gap: 14, alignContent: "start" }}>
                {!settings?.configured && (
                  <div style={{
                    background: "#FFF8E9", border: "1px solid #E9D9A8",
                    borderRadius: 8, padding: "10px 12px",
                    fontSize: 12, color: "#5A4300", lineHeight: 1.4,
                  }}>
                    Sends go through your own Resend account. Sign up free at <b>resend.com</b>,
                    verify <b>hepcure.ca</b> as a domain (3 DNS records), then paste your API
                    key below to unlock sending.
                  </div>
                )}
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4 }}>Resend API key</label>
                  <input
                    type="password"
                    autoComplete="off"
                    value={settingsForm.resend_api_key}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, resend_api_key: e.target.value }))}
                    placeholder={settings?.configured ? "•••••••••••  (leave blank to keep existing)" : "re_XXXXXXXXXXXXXXXXXXXXXXXX"}
                    data-testid="email-blast-key"
                    style={{
                      width: "100%", height: 38, boxSizing: "border-box",
                      padding: "0 12px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: "white",
                      color: TEXT, fontSize: 14, fontFamily: "'JetBrains Mono','Menlo',monospace",
                    }}
                  />
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                    Get one at <b>resend.com/api-keys</b>. Stored securely on the server, never returned to the browser after save.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4 }}>From email</label>
                  <input
                    type="email"
                    value={settingsForm.from_email}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, from_email: e.target.value }))}
                    placeholder="info@hepcure.ca"
                    data-testid="email-blast-from-email"
                    style={{
                      width: "100%", height: 38, boxSizing: "border-box",
                      padding: "0 12px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: "white",
                      color: TEXT, fontSize: 14, fontFamily: "inherit",
                    }}
                  />
                  <div style={{ fontSize: 11, color: MUTED, marginTop: 4 }}>
                    Must be on a domain you&rsquo;ve verified in Resend.
                  </div>
                </div>
                <div>
                  <label style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", color: LABEL, display: "block", marginBottom: 4 }}>From name</label>
                  <input
                    type="text"
                    value={settingsForm.from_name}
                    onChange={(e) => setSettingsForm((f) => ({ ...f, from_name: e.target.value }))}
                    placeholder="HepCURE"
                    data-testid="email-blast-from-name"
                    style={{
                      width: "100%", height: 38, boxSizing: "border-box",
                      padding: "0 12px", borderRadius: 8,
                      border: `1px solid ${BORDER}`, background: "white",
                      color: TEXT, fontSize: 14, fontFamily: "inherit",
                    }}
                  />
                </div>
              </div>
            )}

            {/* Test send inline prompt — appears in compose view */}
            {view === "compose" && testOpen && (
              <div style={{
                padding: "10px 12px",
                background: "#F5F8F6",
                borderTop: `1px solid ${BORDER}`,
                display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
              }}>
                <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", color: LABEL, flexShrink: 0 }}>
                  Send test to
                </div>
                <input
                  type="email"
                  value={testEmail}
                  onChange={(e) => rememberTestEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="email-blast-test-email"
                  style={{
                    flex: 1, minWidth: 140, height: 32, boxSizing: "border-box",
                    padding: "0 10px", borderRadius: 6,
                    border: `1px solid ${isValidTestEmail ? BORDER : RED}`,
                    background: "white", color: TEXT, fontSize: 13, fontFamily: "inherit",
                  }}
                />
                <button
                  type="button"
                  disabled={!isValidTestEmail || !subject.trim() || !body.trim()}
                  onClick={sendTest}
                  data-testid="email-blast-test-send"
                  style={{
                    background: (isValidTestEmail && subject.trim() && body.trim()) ? DARK_GREEN : "#B4BDB8",
                    border: "none", color: "white",
                    height: 32, padding: "0 14px", borderRadius: 6,
                    fontSize: 12.5, fontWeight: 800,
                    cursor: (isValidTestEmail && subject.trim() && body.trim()) ? "pointer" : "not-allowed",
                    whiteSpace: "nowrap", flexShrink: 0,
                  }}
                >
                  Send test
                </button>
              </div>
            )}

            {/* Footer */}
            <div style={{
              padding: "10px 12px", borderTop: `1px solid ${BORDER}`,
              display: "flex", gap: 6, alignItems: "center",
              background: "#FAFAF7", flexWrap: "nowrap",
            }}>
              <button
                type="button"
                onClick={() => setOpen(false)}
                style={{
                  background: "white", border: `1px solid ${BORDER}`,
                  padding: "8px 12px", borderRadius: 8,
                  fontSize: 12.5, fontWeight: 600, color: TEXT, cursor: "pointer", whiteSpace: "nowrap",
                }}
              >
                Cancel
              </button>
              <div style={{ flex: 1 }} />
              {view === "settings" ? (
                <button
                  type="button"
                  onClick={saveSettings}
                  disabled={settingsBusy}
                  data-testid="email-blast-save-settings"
                  style={{
                    background: DARK_GREEN, border: `1px solid ${DARK_GREEN}`, color: "white",
                    padding: "8px 16px", borderRadius: 8,
                    fontSize: 12.5, fontWeight: 800,
                    cursor: settingsBusy ? "wait" : "pointer",
                    opacity: settingsBusy ? 0.7 : 1,
                    display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap",
                  }}
                >
                  <Check style={{ width: 13, height: 13 }} strokeWidth={2.6} />
                  {settingsBusy ? "Saving…" : "Save settings"}
                </button>
              ) : view === "compose" ? (
                <>
                  <button
                    type="button"
                    onClick={() => setTestOpen((v) => !v)}
                    data-testid="email-blast-test-toggle"
                    style={{
                      background: testOpen ? "#E9F1EC" : "white",
                      border: `1px solid ${DARK_GREEN}`, color: DARK_GREEN,
                      padding: "8px 12px", borderRadius: 8,
                      fontSize: 12.5, fontWeight: 700, cursor: "pointer",
                      display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
                    }}
                  >
                    <Eye style={{ width: 13, height: 13 }} strokeWidth={2.2} />
                    Test to me
                  </button>
                  <button
                    type="button"
                    onClick={sendBroadcast}
                    disabled={sending || !settings?.configured || effectiveSendCount === 0}
                    data-testid="email-blast-send"
                    style={{
                      background: (!settings?.configured || effectiveSendCount === 0) ? "#B4BDB8" : DARK_GREEN,
                      border: "none", color: "white",
                      padding: "8px 14px", borderRadius: 8,
                      fontSize: 12.5, fontWeight: 800,
                      cursor: sending ? "wait" : ((!settings?.configured || effectiveSendCount === 0) ? "not-allowed" : "pointer"),
                      display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap",
                    }}
                  >
                    <Send style={{ width: 13, height: 13 }} strokeWidth={2.2} />
                    {sending ? "Sending…" : `Send · ${effectiveSendCount}`}
                  </button>
                </>
              ) : (
                /* List view — primary CTA back to compose in the bottom
                   footer keeps it consistent with the Send button placement
                   in compose view. */
                <button
                  type="button"
                  onClick={() => setView("compose")}
                  data-testid="email-blast-back-to-compose"
                  style={{
                    background: DARK_GREEN, border: "none", color: "white",
                    padding: "8px 14px", borderRadius: 8,
                    fontSize: 12.5, fontWeight: 800, cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  ← Back to compose
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Import dialog */}
      {importOpen && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setImportOpen(false); }}
          style={{
            position: "fixed", inset: 0, zIndex: 100,
            background: "rgba(15,20,17,0.6)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 12,
          }}
        >
          <div style={{
            background: "white", borderRadius: 16,
            width: "min(560px, 100%)", maxHeight: "92vh",
            display: "flex", flexDirection: "column",
            boxShadow: "0 20px 60px rgba(0,0,0,0.35)",
            overflow: "hidden",
          }}>
            <div style={{
              padding: "16px 20px", borderBottom: `1px solid ${BORDER}`,
              display: "flex", alignItems: "flex-start", gap: 12,
            }}>
              <div style={{
                width: 40, height: 40, borderRadius: 10,
                background: "#3E5B96", color: "white",
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
              }}>
                <UserPlus style={{ width: 20, height: 20 }} strokeWidth={2} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, lineHeight: 1.2 }}>Import email list</div>
                <div style={{ fontSize: 12, color: MUTED, marginTop: 3 }}>
                  Upload a spreadsheet from a prior system — deduped on save.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                aria-label="Close"
                style={{ background: "none", border: "none", padding: 6, color: MUTED, cursor: "pointer", borderRadius: 6 }}
              >
                <X style={{ width: 16, height: 16 }} strokeWidth={2.2} />
              </button>
            </div>

            <div style={{ padding: 20, overflowY: "auto", display: "grid", gap: 14 }}>
              {/* Mode toggle — file (default) vs paste text (iOS-safe fallback). */}
              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6,
                background: "#F1EFE8", padding: 4, borderRadius: 10,
              }}>
                {[
                  { k: "file",  label: "Upload file" },
                  { k: "paste", label: "Paste emails" },
                ].map((m) => {
                  const active = importMode === m.k;
                  return (
                    <button
                      key={m.k}
                      type="button"
                      onClick={() => {
                        setImportMode(m.k);
                        setImportFile(null);
                        setImportPreview(null);
                        setImportPaste("");
                      }}
                      data-testid={`email-blast-mode-${m.k}`}
                      style={{
                        background: active ? "white" : "transparent",
                        border: "none", padding: "7px 10px", borderRadius: 8,
                        fontSize: 12.5, fontWeight: 700,
                        color: active ? TEXT : MUTED, cursor: "pointer",
                        boxShadow: active ? "0 1px 2px rgba(0,0,0,0.08)" : "none",
                      }}
                    >{m.label}</button>
                  );
                })}
              </div>

              {importMode === "file" ? (
              <label
                htmlFor="email-blast-file-input"
                style={{
                  display: "flex", flexDirection: "column",
                  alignItems: "center", justifyContent: "center",
                  gap: 8, padding: "24px 20px",
                  border: `2px dashed ${BORDER}`, borderRadius: 12,
                  background: "#FAFAF7", cursor: "pointer", textAlign: "center",
                }}
              >
                <div style={{
                  width: 44, height: 44, borderRadius: 12,
                  background: "rgba(62,91,150,0.10)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "#3E5B96",
                }}>
                  <Upload style={{ width: 20, height: 20 }} strokeWidth={2} />
                </div>
                <div style={{ fontSize: 14, fontWeight: 700, color: TEXT }}>
                  {importBusy ? "Parsing…" : "Drop your file here, or tap to browse"}
                </div>
                <div style={{ fontSize: 12, color: MUTED, maxWidth: 320 }}>
                  Accepts <b>.xlsx</b>, <b>.xls</b> or <b>.csv</b> · one email per row · up to 10 000 rows
                </div>
                <input
                  id="email-blast-file-input"
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  data-testid="email-blast-file-input"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChosen(e.target.files?.[0])}
                />
              </label>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  <textarea
                    value={importPaste}
                    onChange={(e) => setImportPaste(e.target.value)}
                    data-testid="email-blast-paste-textarea"
                    placeholder={"jane@example.com\njohn@example.com\n…"}
                    rows={8}
                    style={{
                      width: "100%", resize: "vertical",
                      border: `1px solid ${BORDER}`, borderRadius: 10,
                      padding: "10px 12px", fontSize: 13, fontFamily: "'JetBrains Mono','Menlo',monospace",
                      background: "#FAFAF7", color: TEXT, minHeight: 140,
                      outline: "none",
                    }}
                  />
                  <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.5 }}>
                    One email per line (or comma-separated). Works on any device — no file picker required.
                  </div>
                  <button
                    type="button"
                    onClick={handlePasteImport}
                    disabled={importBusy || !importPaste.trim()}
                    data-testid="email-blast-paste-preview"
                    style={{
                      background: importPaste.trim() && !importBusy ? DARK_GREEN : "#B4BDB8",
                      color: "white", border: "none", padding: "8px 14px", borderRadius: 8,
                      fontSize: 13, fontWeight: 700,
                      cursor: importPaste.trim() && !importBusy ? "pointer" : "not-allowed",
                      justifySelf: "start",
                    }}
                  >
                    {importBusy ? "Parsing…" : "Preview list"}
                  </button>
                </div>
              )}

              {importPreview && (
                <div style={{
                  border: `1px solid ${BORDER}`, borderRadius: 10,
                  background: "#F5F8F6", padding: 12,
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: TEXT, fontWeight: 700 }}>
                    <FileSpreadsheet style={{ width: 15, height: 15, color: "#3E5B96" }} strokeWidth={2.2} />
                    {importPreview.filename}
                    <span style={{ marginLeft: "auto", fontSize: 11, color: MUTED, fontWeight: 500 }}>
                      {Math.round((importPreview.size_bytes || 0) / 1024)}&nbsp;KB
                    </span>
                  </div>
                  <div style={{ marginTop: 8, display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 6, fontSize: 12 }}>
                    {[
                      { k: "found",       label: "Found",     color: TEXT },
                      { k: "duplicates",  label: "Duplicates",color: MUTED },
                      { k: "kept_unsub",  label: "Kept unsub.", color: RED, outline: true },
                      { k: "will_add",    label: "Will add",  color: DARK_GREEN },
                    ].map((c) => (
                      <div key={c.k} style={{
                        background: "white", padding: "6px 4px", borderRadius: 8, textAlign: "center",
                        outline: c.outline ? `1.5px solid ${RED}` : "none",
                      }}>
                        <div style={{ fontSize: 15, fontWeight: 800, color: c.color }}>
                          {importPreview.counts[c.k] ?? 0}
                        </div>
                        <div style={{ fontSize: 9.5, color: c.outline ? RED : MUTED, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase" }}>
                          {c.label}
                        </div>
                      </div>
                    ))}
                  </div>
                  {(importPreview.counts?.kept_unsub || 0) > 0 && (
                    <div style={{
                      marginTop: 8, padding: "6px 10px",
                      background: "rgba(180,53,27,0.06)", border: "1px solid rgba(180,53,27,0.18)",
                      borderRadius: 8, fontSize: 11, color: "#7A2917", lineHeight: 1.4,
                      display: "flex", alignItems: "flex-start", gap: 6,
                    }}>
                      <Info style={{ width: 12, height: 12, flexShrink: 0, marginTop: 1 }} strokeWidth={2.4} />
                      <span>
                        <b>{importPreview.counts.kept_unsub} addresses</b> previously unsubscribed — they stay excluded.
                        Opt-outs are stored per email and survive every re-import.
                      </span>
                    </div>
                  )}
                  {importPreview.rows?.length > 0 && (
                    <div style={{
                      marginTop: 10, background: "white", borderRadius: 8,
                      border: `1px solid ${BORDER}`, overflow: "hidden", maxHeight: 220, overflowY: "auto",
                    }}>
                      {importPreview.rows.slice(0, 50).map((row, i) => {
                        const status = row.status;
                        const color = status === "unsub" ? RED : status === "dup" ? MUTED : DARK_GREEN;
                        const bg    = status === "unsub" ? "rgba(180,53,27,0.10)" : status === "dup" ? "#F1EFE8" : "rgba(15,59,46,0.08)";
                        const tag   = status === "unsub" ? "Kept unsub." : status === "dup" ? "Duplicate" : "New";
                        return (
                          <div key={row.email + i} style={{
                            display: "flex", alignItems: "center", justifyContent: "space-between",
                            padding: "6px 10px", borderTop: i === 0 ? "none" : `1px solid ${BORDER}`,
                            fontSize: 12, color: status !== "new" ? MUTED : TEXT,
                          }}>
                            <span style={{
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                              textDecoration: status === "unsub" ? "line-through" : "none",
                            }}>{row.email}</span>
                            <span style={{
                              fontSize: 10, fontWeight: 800, letterSpacing: "0.12em",
                              textTransform: "uppercase", color, background: bg,
                              padding: "2px 7px", borderRadius: 999, flexShrink: 0, marginLeft: 8,
                            }}>{tag}</span>
                          </div>
                        );
                      })}
                      {importPreview.rows.length > 50 && (
                        <div style={{
                          padding: "6px 10px", borderTop: `1px solid ${BORDER}`,
                          fontSize: 11, color: MUTED, textAlign: "center", fontStyle: "italic",
                        }}>
                          + {importPreview.rows.length - 50} more rows
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div style={{
                background: "#FFF8E9", border: "1px solid #E9D9A8",
                borderRadius: 8, padding: "10px 12px",
                display: "flex", alignItems: "flex-start", gap: 8,
                fontSize: 12, color: "#5A4300", lineHeight: 1.4,
              }}>
                <Info style={{ width: 14, height: 14, flexShrink: 0, marginTop: 1 }} strokeWidth={2.2} />
                <div>
                  Only import addresses you have <b>express or implied consent</b> to email (CASL).
                  Each imported recipient gets the same unsubscribe link as adherence-collected ones.
                </div>
              </div>
            </div>

            <div style={{
              padding: 12, borderTop: `1px solid ${BORDER}`, background: "#FAFAF7",
              display: "flex", gap: 8, alignItems: "center",
            }}>
              <button
                type="button"
                onClick={() => setImportOpen(false)}
                style={{
                  background: "white", border: `1px solid ${BORDER}`,
                  padding: "8px 14px", borderRadius: 8,
                  fontSize: 13, fontWeight: 600, color: TEXT, cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <div style={{ flex: 1 }} />
              <button
                type="button"
                onClick={confirmImport}
                disabled={!importPreview || importBusy || (importPreview.counts?.will_add ?? 0) === 0}
                data-testid="email-blast-import-confirm"
                style={{
                  background: (importPreview && (importPreview.counts?.will_add ?? 0) > 0) ? DARK_GREEN : "#B4BDB8",
                  border: "none", color: "white",
                  padding: "8px 16px", borderRadius: 8,
                  fontSize: 13, fontWeight: 800,
                  cursor: (importPreview && (importPreview.counts?.will_add ?? 0) > 0 && !importBusy) ? "pointer" : "not-allowed",
                  display: "inline-flex", alignItems: "center", gap: 6,
                }}
              >
                <Check style={{ width: 14, height: 14 }} strokeWidth={2.6} />
                {importBusy ? "Working…" : `Add ${importPreview?.counts?.will_add ?? 0} recipients`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
